import { getAuthToken, notifyAuthLost, refreshAuthToken } from "./auth";
import { withDeviceHeader } from "./device";

export const API_URL =
    import.meta.env.VITE_API_URL ??
    import.meta.env.VITE_API_HTTP_URL ?? // backward compat
    '/api';

function handleUnauthorized() {
    notifyAuthLost();
    throw new Error("Unauthorized");
}

function withAuthHeader(token: string | null | undefined, headers?: HeadersInit): HeadersInit {
    const out = withDeviceHeader(headers);
    if (token) out.set('Authorization', `Bearer ${token}`);
    return out;
}

async function authFetch(path: string, init: RequestInit = {}, tokenOverride?: string | null, allowRetry = true): Promise<Response> {
    const token = tokenOverride ?? getAuthToken();
    const res = await fetch(`${API_URL}${path}`, {
        ...init,
        credentials: 'include',
        headers: withAuthHeader(token, init.headers),
    });
    if (res.status !== 401 || !allowRetry) return res;

    const nt = await refreshAuthToken();
    if (!nt) handleUnauthorized();
    return fetch(`${API_URL}${path}`, {
        ...init,
        credentials: 'include',
        headers: withAuthHeader(nt, init.headers),
    });
}

export const api = {
    async post(path: string, body: any) {
        const res = await fetch(`${API_URL}${path}`, {
            method: 'POST',
            credentials: 'include',
            headers: withDeviceHeader({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            let msg = 'Request failed';
            let code: string | undefined;
            try {
                const data = await res.json();
                msg = data?.message ?? msg;
                code = typeof data?.code === 'string' ? data.code : undefined;
            } catch {}
            const err = new Error(msg) as Error & { code?: string };
            if (code) err.code = code;
            throw err;
        }
        return res.json();
    },
    async deleteAuth(path: string, body: any, token?: string) {
        const res = await authFetch(path, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }, token);
        if (!res.ok) {
            let msg = 'Request failed';
            try { msg = (await res.json())?.message ?? msg; } catch {}
            throw new Error(msg);
        }
        // Some endpoints return 204 No Content; avoid JSON.parse on empty body
        if (res.status === 204) return {} as any;
        try { return await res.json(); } catch { return {} as any; }
    },
    async postAuth(path: string, body: any, token?: string) {
        const res = await authFetch(path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }, token);
        if (!res.ok) {
            let msg = 'Request failed';
            try { msg = (await res.json())?.message ?? msg; } catch {}
            const err = new Error(msg) as Error & { status?: number };
            err.status = res.status;
            throw err;
        }
        return res.json();
    },
    async putAuth(path: string, body: any, token?: string) {
        const res = await authFetch(path, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        }, token);
        if (!res.ok) {
            let msg = 'Request failed';
            try { msg = (await res.json())?.message ?? msg; } catch {}
            throw new Error(msg);
        }
        return res.json();
    },
    async getAuth(path: string, token?: string) {
        const res = await authFetch(path, { method: 'GET' }, token);
        if (!res.ok) {
            let msg = 'Request failed';
            try { msg = (await res.json())?.message ?? msg; } catch {}
            const err = new Error(msg) as Error & { status?: number };
            err.status = res.status;
            throw err;
        }
        return res.json();
    },
    async patchAuth(path: string, body: any, token?: string) {
        const res = await authFetch(path, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, token);
        if (!res.ok) {
            let msg = 'Request failed';
            try { msg = (await res.json())?.message ?? msg; } catch {}
            throw new Error(msg);
        }
        return res.json();
    },
};

export async function signUpload(params: { filename: string; contentType: string; size: number; purpose?: 'message' }, token?: string) {
    const res = await authFetch('/files/sign', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
    }, token);
    if (!res.ok) {
        let msg = 'Sign failed';
        try { msg = (await res.json())?.message ?? msg; } catch {}
        throw new Error(msg);
    }
    const data = await res.json();
    // Defensive normalization: ensure publicUrl is an absolute/same-origin path
    try {
        let pub = data?.publicUrl as string | undefined;
        const key = (data?.key as string | undefined) || '';
        const hasProtoOrRoot = typeof pub === 'string' && /^(https?:)?\//.test(pub);
        if (!hasProtoOrRoot) {
            const cleanKey = String(key || pub || '').replace(/^\/+/, '');
            pub = `/files/echo-app/${cleanKey}`;
        }
        return { ...data, publicUrl: pub };
    } catch {
        return data;
    }
}
